/**
 * Screen 1g — one bundle.
 *
 * The hero, the courses in their order, and who else is on it. An ordered
 * bundle locks each course until the one before it is finished; `unlocked` is
 * the server's answer, not a sum this page performs, because the rule reaches
 * back into enrolments this page never sees.
 *
 * "Also on this bundle" is initials. Real avatars would need every member's
 * photo on a screen that is about the bundle, which the design critic settled.
 */

import { useState } from 'react';
import { learn } from '../api';
import { AssignDialog } from '../components/AssignDialog';
import { Avatar, CourseMark, DeadlinePill, ProgressLine } from '../components/Bits';
import { ErrorNote, Loading, OkNote, PageTitle, Panel, PanelHead } from '../components/Chrome';
import { ds } from '../ds';
import { Link, useRouter } from '../router';
import { HAIR, meta, minutesLabel, monoPlain, plural } from '../theme';
import { useLoad } from '../useLoad';

export default function Bundle({ id }: { id: string }) {
  const { Badge, Button, Icon } = ds();
  const { navigate } = useRouter();
  const { data, error, loading, reload } = useLoad(
    () => learn.bundle(id),
    'Could not load this bundle.',
    id
  );
  const [assigning, setAssigning] = useState(false);
  const [ok, setOk] = useState<string | null>(null);

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!data) return loading ? <Loading /> : null;

  const { bundle, courses, alsoOn } = data;

  return (
    <>
      <PageTitle
        title={bundle.title}
        meta={meta(
          bundle.ordered ? 'Bundle · in order' : 'Bundle',
          plural(bundle.courseCount, 'course'),
          `${bundle.completedCount} done`
        )}
      >
        {data.canTeach && (
          <>
            <Button variant="secondary" size="sm" onClick={() => setAssigning(true)}>
              Assign this bundle
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate(`/teach/bundles/${bundle.id}`)}>
              See the bundle report
            </Button>
          </>
        )}
      </PageTitle>

      <OkNote>{ok}</OkNote>

      <Panel style={{ display: 'grid', gap: 14 }}>
        {bundle.description && (
          <p style={{ margin: 0, fontSize: 15, lineHeight: 1.6, color: 'var(--text-body)' }}>
            {bundle.description}
          </p>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'grid', gap: 6, minWidth: 220, flex: 1 }}>
            <span style={monoPlain}>Your progress</span>
            <ProgressLine percent={bundle.percent} />
          </div>
          <DeadlinePill badge={data.badge} dueAt={data.dueAt} daysLate={data.daysLate} />
          {data.assignedBy && <span style={monoPlain}>Assigned by {data.assignedBy.name}</span>}
        </div>
      </Panel>

      <Panel style={{ display: 'grid', gap: 4 }}>
        <PanelHead title="Courses" meta={bundle.ordered ? 'Unlock as you go' : null} />

        {courses.map((entry) => {
          const { course, enrolment, unlocked } = entry;
          const finished = !!enrolment?.completedAt;

          return (
            <div
              key={course.id}
              className="mv-row"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '14px 4px',
                borderTop: HAIR,
                opacity: unlocked ? 1 : 0.55
              }}
            >
              <span style={{ ...monoPlain, width: 22, textAlign: 'right' }}>{entry.position + 1}</span>
              <CourseMark colour={course.colour} />

              <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: 3 }}>
                <Link to={`/course/${course.id}`}>
                  <span style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-strong)' }}>
                    {course.title}
                  </span>
                </Link>
                <span style={monoPlain}>
                  {meta(
                    course.lessonCount === null ? null : plural(course.lessonCount, 'lesson'),
                    minutesLabel(course.minutes)
                  )}
                </span>
              </div>

              {finished ? (
                <Badge tone="success" icon="Check">
                  Done
                </Badge>
              ) : (
                <ProgressLine percent={enrolment?.percent ?? 0} width={120} />
              )}

              {unlocked ? (
                <Button
                  variant={finished ? 'ghost' : 'primary'}
                  size="sm"
                  onClick={() => navigate(`/course/${course.id}/play`)}
                >
                  {finished ? 'Open again' : enrolment ? 'Continue' : 'Start'}
                </Button>
              ) : (
                <span style={{ ...monoPlain, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Icon name="Lock" size={13} />
                  Locked
                </span>
              )}
            </div>
          );
        })}
      </Panel>

      {alsoOn.length > 0 && (
        <Panel style={{ display: 'grid', gap: 12 }}>
          <PanelHead title="Also on this bundle" meta={plural(alsoOn.length, 'person', 'people')} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {alsoOn.map((person) => (
              <span
                key={person.id}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}
              >
                <Avatar name={person.name} />
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{person.name}</span>
              </span>
            ))}
          </div>
        </Panel>
      )}

      {assigning && (
        <AssignDialog
          bundleId={bundle.id}
          subject={bundle.title}
          onClose={() => setAssigning(false)}
          onDone={(result) => {
            setAssigning(false);
            setOk(
              `Assigned to ${plural(result.assigned, 'person', 'people')}.${
                result.alreadyAssigned > 0 ? ` ${result.alreadyAssigned} already had it.` : ''
              }`
            );
            void reload();
          }}
        />
      )}
    </>
  );
}
